import { useState, useEffect } from "react";
import type { Category, Account, CostCenter, PaymentMethod, Supplier, Transaction } from "@/context/FinanceContext";

const STORAGE_KEY = "finance-store-v3";

export interface PersistedState {
  categories: Category[];
  accounts: Account[];
  transactions: Transaction[];
  costCenters: CostCenter[];
  paymentMethods: PaymentMethod[];
  suppliers: Supplier[];
}

// Empty initial state - users start with clean data
export const emptySeed: PersistedState = {
  categories: [],
  accounts: [],
  transactions: [],
  costCenters: [],
  paymentMethods: [],
  suppliers: [],
};

export function useFinanceState(userId: string | undefined) {
  const [state, setState] = useState<PersistedState>(emptySeed);
  const [referenceDate, setReferenceDate] = useState<Date>(new Date());

  // localStorage apenas como cache read-only - NÃO é fonte de dados
  // Dados sempre vêm do Supabase quando usuário está logado
  useEffect(() => {
    if (!userId) return; // Não cachear quando não houver usuário logado

    const id = window.setTimeout(() => {
      try {
        // Guard: nunca sobrescrever cache populado com estado vazio
        // (evita "envenenamento" durante flickers de auth/sync)
        if (state.transactions.length === 0) {
          try {
            const existing = localStorage.getItem(STORAGE_KEY);
            if (existing) {
              const parsed = JSON.parse(existing);
              if (Array.isArray(parsed?.transactions) && parsed.transactions.length > 0) {
                return;
              }
            }
          } catch {}
        }

        const persist = () => {
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
        };
        if ('requestIdleCallback' in window) {
          (window as any).requestIdleCallback(persist);
        } else {
          persist();
        }
      } catch {}
    }, 500); // Cache apenas após estabilização
    return () => clearTimeout(id);
  }, [state, userId]);


  // Ensure hidden 'Transferência' category exists
  useEffect(() => {
    setState((s) => {
      const existing = s.categories.find((c) => c.name.toLowerCase() === "transferência");
      if (!existing) {
        return { ...s, categories: [...s.categories, { id: `cat-${crypto.randomUUID()}`, name: "Transferência", color: "#64748b", hidden: true }] };
      }
      if (!existing.hidden) {
        return { ...s, categories: s.categories.map((c) => (c.id === existing.id ? { ...c, hidden: true } : c)) };
      }
      return s;
    });
  }, [state.categories]);

  return {
    state,
    setState,
    referenceDate,
    setReferenceDate,
  };
}
